/**
 * AvizorLaunchModal — diálogo para que un gestor lance un Avizor
 * (videoperitación inmediata) sobre un expediente.
 *
 * Inputs:
 *   - tipo: asegurado | reparador
 *   - perito asignado (lista filtrada a perito_interno/perito_externo activos)
 *   - contact_name / contact_phone / contact_email (prefill desde el expediente)
 *   - channel: sms | whatsapp | email
 *   - message opcional
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  X, VideoCamera, Phone, EnvelopeSimple, ChatCircleDots,
  ClipboardText, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { pickMobileFirst } from "@/lib/phones";
import { ROLES_PERITO_LIKE } from "@/lib/roles";

// Roles que pueden recibir un Avizor (un admin con `code` también).
const PERITO_ROLES = ROLES_PERITO_LIKE;

const DEFAULT_ASEGURADORA_TEMPLATE = (ctx) => {
  const numero = ctx.numero_siniestro || "—";
  const asegurado = ctx.asegurado || "—";
  const perito = ctx.perito_nombre || "el perito asignado";
  return (
    `Buenos días,\n` +
    `Hemos lanzado un Avizor (videoperitación inmediata) sobre el siniestro ${numero} ` +
    `del asegurado ${asegurado}.\n` +
    `${perito} contactará en los próximos minutos con el ${ctx.tipo === "reparador" ? "reparador" : "asegurado"} ` +
    `para realizar la inspección por videollamada.\n` +
    `Os adelantamos que la inspección queda registrada en el expediente como videoperitaje.\n` +
    `Un saludo.`
  );
};

export default function AvizorLaunchModal({ expediente, peritos = [], onClose, onLaunched }) {
  const expId = expediente?.id;

  // Filtrar lista de peritos elegibles (asignables). Lo subimos para usarlo
  // al calcular el default `perito_id` (mapeando desde el `perito_codigo`).
  const elegibles = useMemo(
    () => peritos.filter((p) => p.is_active !== false && PERITO_ROLES.has(p.role)),
    [peritos],
  );

  // Estado inicial calculado UNA SOLA VEZ por expediente (clave `expId`).
  // El default del perito es el ya asignado al expediente
  // (`extracted_data.perito_codigo`). Si no hay asignado o no se encuentra
  // en la lista de elegibles, se deja vacío para forzar al gestor a elegir.
  const initial = useMemo(() => {
    const ed0 = expediente?.extracted_data || {};
    // Para Avizor SIEMPRE preferimos el móvil — los proveedores mandan SMS.
    // Se considera móvil un número español que empieza por 6 o 7. Si no se
    // encuentra ningún móvil, caemos al primer no-vacío disponible.
    const tel = pickMobileFirst(ed0.asegurado_movil, ed0.asegurado_telefono);
    const codigo = (ed0.perito_codigo || "").toUpperCase();
    const peritoAsignado = codigo
      ? elegibles.find((p) => (p.code || "").toUpperCase() === codigo)
      : null;
    return {
      tipo: "asegurado",
      perito_id: peritoAsignado?.id || "",
      contact_name: ed0.asegurado_nombre || "",
      contact_phone: tel,
      contact_email: ed0.asegurado_email || "",
      channel: "sms",
      message: "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expId, elegibles.length]);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset SOLO cuando cambia el expediente (no en cada render del padre).
  useEffect(() => {
    setForm(initial);
  }, [initial]);

  // Plantilla "aviso a aseguradora" — editable inline antes de copiar.
  const peritoActual = elegibles.find((p) => p.id === form.perito_id);
  const ed = expediente?.extracted_data || {};
  const defaultAvisoText = useMemo(
    () =>
      DEFAULT_ASEGURADORA_TEMPLATE({
        numero_siniestro: ed.numero_siniestro,
        asegurado: ed.asegurado_nombre,
        perito_nombre: peritoActual?.name,
        tipo: form.tipo,
      }),
    [ed.numero_siniestro, ed.asegurado_nombre, peritoActual?.name, form.tipo],
  );
  const [avisoText, setAvisoText] = useState(defaultAvisoText);
  const [avisoEdited, setAvisoEdited] = useState(false);
  // Si el gestor no ha tocado el texto manualmente, lo mantenemos sincronizado
  // con la plantilla cuando cambien los datos del expediente o el tipo.
  useEffect(() => {
    if (!avisoEdited) setAvisoText(defaultAvisoText);
  }, [defaultAvisoText, avisoEdited]);

  const copyAviso = async () => {
    try {
      await navigator.clipboard.writeText(avisoText);
      setCopied(true);
      toast.success("Texto copiado al portapapeles");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar (usa Cmd/Ctrl+C)");
    }
  };

  // Si el usuario cambia el tipo a "reparador", refrescamos el prefill
  const onTipoChange = (tipo) => {
    setForm((f) => {
      if (tipo === f.tipo) return f;
      if (tipo === "reparador") {
        return { ...f, tipo, contact_name: "", contact_phone: "", contact_email: "" };
      }
      const ed0 = expediente?.extracted_data || {};
      const tel = ed0.asegurado_telefono || ed0.asegurado_movil || "";
      return {
        ...f,
        tipo,
        contact_name: ed0.asegurado_nombre || "",
        contact_phone: tel,
        contact_email: ed0.asegurado_email || "",
      };
    });
  };

  const canSubmit =
    !!form.perito_id &&
    ((form.channel === "email" && !!form.contact_email) ||
      (form.channel !== "email" && !!form.contact_phone));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/avizor", {
        expediente_id: expediente.id,
        ...form,
      });
      toast.success("Avizor enviado — esperando respuesta del perito");
      onLaunched?.(data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo lanzar el Avizor";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 p-4"
      data-testid="avizor-launch-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-xl shadow-2xl w-[min(560px,96vw)] max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-status-error-fg/10 text-status-error-fg flex items-center justify-center">
              <VideoCamera className="w-4 h-4" weight="duotone" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">
                Videoperitación inmediata
              </div>
              <div className="text-[14px] font-semibold text-foreground">Lanzar Avizor</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="avizor-launch-close"
            className="p-1.5 hover:bg-background rounded-md text-muted hover:text-foreground"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" weight="bold" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Tipo */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
              Tipo de inspección
            </label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[
                { v: "asegurado", l: "Con el asegurado" },
                { v: "reparador", l: "Con el reparador" },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => onTipoChange(t.v)}
                  data-testid={`avizor-tipo-${t.v}`}
                  className={`text-[12.5px] border rounded-lg px-3 py-2 font-semibold transition-colors ${
                    form.tipo === t.v
                      ? "border-brand bg-brand/5 text-brand"
                      : "border-border bg-white text-foreground hover:bg-background"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          {/* Perito */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
              Perito asignado
            </label>
            <select
              value={form.perito_id}
              onChange={(e) => setForm({ ...form, perito_id: e.target.value })}
              data-testid="avizor-perito-select"
              className="mt-1.5 w-full text-[13px] border border-border rounded-lg px-2.5 py-2 bg-white focus:border-brand outline-none"
              required
            >
              <option value="">— Selecciona un perito —</option>
              {elegibles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
            {elegibles.length === 0 && (
              <div className="mt-1.5 text-[11px] text-status-warning-fg">
                No hay peritos elegibles activos.
              </div>
            )}
          </div>

          {/* Contacto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                Nombre del contacto
              </label>
              <input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="avizor-contact-name"
                placeholder={form.tipo === "reparador" ? "Taller, reparador..." : "Asegurado"}
                className="mt-1.5 w-full text-[13px] border border-border rounded-lg px-2.5 py-2 bg-white focus:border-brand outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                Teléfono
              </label>
              <input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                data-testid="avizor-contact-phone"
                placeholder="+34 600 000 000"
                className="mt-1.5 w-full text-[13px] border border-border rounded-lg px-2.5 py-2 bg-white focus:border-brand outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                Email (opcional)
              </label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                data-testid="avizor-contact-email"
                placeholder="contacto@ejemplo.com"
                className="mt-1.5 w-full text-[13px] border border-border rounded-lg px-2.5 py-2 bg-white focus:border-brand outline-none"
              />
            </div>
          </div>

          {/* Canal */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
              Canal de invitación al asegurado/reparador
            </label>
            <div className="mt-1.5 grid grid-cols-3 gap-2">
              {[
                { v: "sms", l: "SMS", I: Phone },
                { v: "whatsapp", l: "WhatsApp", I: ChatCircleDots },
                { v: "email", l: "Email", I: EnvelopeSimple },
              ].map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setForm({ ...form, channel: c.v })}
                  data-testid={`avizor-channel-${c.v}`}
                  className={`text-[12.5px] border rounded-lg px-3 py-2 font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                    form.channel === c.v
                      ? "border-brand bg-brand/5 text-brand"
                      : "border-border bg-white text-foreground hover:bg-background"
                  }`}
                >
                  <c.I className="w-3.5 h-3.5" weight="regular" />
                  {c.l}
                </button>
              ))}
            </div>
          </div>

          {/* Aviso a la aseguradora — copy/paste antes de lanzar */}
          <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3" data-testid="avizor-aseguradora-box">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold">
                Aviso para la aseguradora (copia y pega antes de lanzar)
              </label>
              <button
                type="button"
                onClick={copyAviso}
                data-testid="avizor-aseguradora-copy"
                className={`text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border font-semibold transition-colors ${
                  copied
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-amber-900 border-amber-300 hover:bg-amber-50"
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" weight="bold" /> : <ClipboardText className="w-3.5 h-3.5" weight="bold" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <textarea
              rows={5}
              value={avisoText}
              onChange={(e) => { setAvisoText(e.target.value); setAvisoEdited(true); }}
              data-testid="avizor-aseguradora-text"
              className="w-full text-[12px] border border-amber-200 rounded-md px-2.5 py-2 bg-white focus:border-amber-400 outline-none resize-vertical font-mono"
            />
            {avisoEdited && (
              <button
                type="button"
                onClick={() => { setAvisoEdited(false); setAvisoText(defaultAvisoText); }}
                data-testid="avizor-aseguradora-reset"
                className="mt-1 text-[10.5px] text-amber-800 hover:underline"
              >
                Restablecer plantilla
              </button>
            )}
          </div>

          {/* Mensaje */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted font-semibold">
              Mensaje para el perito (opcional)
            </label>
            <textarea
              rows={2}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="avizor-message"
              placeholder="Ej.: fuga activa, hay que entrar ya"
              className="mt-1.5 w-full text-[13px] border border-border rounded-lg px-2.5 py-2 bg-white focus:border-brand outline-none resize-none"
            />
          </div>

          <div className="text-[11px] text-muted bg-background border border-border rounded-md px-3 py-2">
            El perito recibirá un aviso con alerta sonora y tendrá <strong>60 s</strong> para
            aceptar antes de que expire. Al lanzar el Avizor se marca el <strong>contacto</strong> hoy
            y la <strong>fecha prevista de visita en 4 minutos</strong>. Si el perito acepta, se crea
            la sala de videoperitaje, el subencargo
            <strong> {form.tipo === "reparador" ? "«Avizor reparador»" : "«Avizor asegurado»"}</strong>{" "}
            y la visita queda marcada como <strong>realizada</strong>.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] px-3 h-9 border border-border bg-white rounded-md hover:bg-background"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="avizor-launch-submit"
            className="text-[13px] px-4 h-9 bg-status-error-fg hover:opacity-90 rounded-md text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <VideoCamera className="w-4 h-4" weight="fill" />
            {submitting ? "Enviando..." : "Lanzar Avizor"}
          </button>
        </div>
      </form>
    </div>
  );
}
